import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { formatDayMonthYear } from '@lib/format_date';
import { usePartnerRoles } from '@hooks/usePartnerRoles';
import { usePartnerTeamQuery } from '@hooks/requests/queries/use_partner_team_query';
import type { DashboardMember } from '@hooks/requests/queries/use_partner_team_query';
import { usePartnerTeamInvitationsQuery } from '@hooks/requests/queries/use_partner_team_invitations_query';
import { useRevokeInvitationMutation } from '@hooks/requests/queries/use_revoke_invitation_mutation';
import { useTheme } from '@hooks/useTheme';
import { useToast } from '@hooks/useToast';
import { Avatar, RoleTag } from '../components/ui';
import { avatarColorIndex } from '../components/ui/avatar_color';
import { ProfileSkeleton } from '../components/dashboard';
import {
  TeamTable,
  InviteModal,
  UpdateRoleModal,
  RemoveConfirmationModal,
} from '../components/account';

/**
 * A DS card (the API Keys page's `.api-keys-card`) with its uppercase eyebrow
 * — the prototype's `.au-card` + `.card-kicker`.
 */
const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="api-keys-card" aria-label={title}>
    <p className="account-eyebrow">{title}</p>
    {children}
  </section>
);

const AccountPage: React.FC = () => {
  const { user, isLoading } = useAuth0();
  const { displayRole, isAdmin, isAceUpStaff, isLoading: rolesLoading } = usePartnerRoles();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { showToast } = useToast();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<DashboardMember | null>(null);
  const [removingMember, setRemovingMember] = useState<DashboardMember | null>(null);
  const teamQuery = usePartnerTeamQuery({ enabled: isAdmin && !rolesLoading });
  const invitationsQuery = usePartnerTeamInvitationsQuery({ enabled: isAdmin && !rolesLoading });
  const revokeInvitationMutation = useRevokeInvitationMutation();

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      await revokeInvitationMutation.mutateAsync({ invitationId });
      showToast('Invitation revoked');
    } catch {
      // The mutation's own error surfaces through invitationsQuery/React
      // Query state on the next render; nothing further to show here beyond
      // not toasting a success that did not happen.
    }
  };

  // "Member Since" comes from the caller's own row in the roster, which is the
  // only place the dashboard learns an Auth0 account's creation date. Viewers
  // cannot read the roster, so for them this stays an em dash until an
  // endpoint serves a caller their own membership (deferred with /partner/me).
  const ownMembership = teamQuery.data?.find((member) => member.userId === user?.sub) ?? null;

  if (isLoading) {
    return <ProfileSkeleton />;
  }

  if (!user) {
    return (
      <div className="account-page">
        <h1 className="api-keys-title">Account</h1>
        <p>Unable to load account information.</p>
      </div>
    );
  }

  const displayName = user.name || user.email || 'User';
  const colorIndex = avatarColorIndex(user.sub || user.email || displayName);
  const isDark = resolvedTheme === 'dark';

  return (
    <div className="account-page">
      <h1 className="api-keys-title">Account</h1>

      {/* Account Info — prototype `.account-info` + `.info-grid`. */}
      <SectionCard title="Account Info">
        <div className="account-info">
          <Avatar size="xl" src={user.picture} name={displayName} colorIndex={colorIndex} />

          <dl className="account-info-grid">
            <div>
              <dt>Name</dt>
              <dd>{user.name || 'Not provided'}</dd>
            </div>
            <div>
              <dt>Role</dt>
              <dd>{rolesLoading || !displayRole ? '—' : <RoleTag displayRole={displayRole} />}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{user.email || 'Not provided'}</dd>
            </div>
            <div>
              <dt>Member since</dt>
              <dd data-testid="member-since">
                {rolesLoading || teamQuery.isLoading
                  ? '—'
                  : formatDayMonthYear(ownMembership?.joinedAt ?? null)}
              </dd>
            </div>
          </dl>
        </div>
      </SectionCard>

      {/* Team — admins only; viewers never see the roster (sc-43656). */}
      {isAdmin && (
        <SectionCard title="Team">
          <TeamTable
            members={teamQuery.data ?? []}
            invitations={invitationsQuery.data ?? []}
            currentUserId={user.sub}
            callerIsAceUpStaff={isAceUpStaff}
            isLoading={teamQuery.isLoading}
            error={teamQuery.error}
            onInvite={() => setIsInviteModalOpen(true)}
            onRevokeInvitation={handleRevokeInvitation}
            onEditRole={setEditingMember}
            onRemoveMember={setRemovingMember}
          />
          <InviteModal isOpen={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} />
          <UpdateRoleModal
            isOpen={editingMember !== null}
            onClose={() => setEditingMember(null)}
            member={editingMember}
          />
          <RemoveConfirmationModal
            isOpen={removingMember !== null}
            onClose={() => setRemovingMember(null)}
            member={removingMember}
          />
        </SectionCard>
      )}

      {/* Preferences — prototype `.pref-row` + `.au-switch`. */}
      <SectionCard title="Preferences">
        <div className="account-pref-row">
          <div className="account-pref-copy">
            <h3 className="account-pref-title">Dark mode</h3>
            <p className="account-pref-description">Switch between light and dark interface</p>
          </div>
          <div className="account-switch-wrap">
            <span className="account-switch-label">{isDark ? 'On' : 'Off'}</span>
            <button
              type="button"
              role="switch"
              aria-checked={isDark}
              aria-label="Dark mode"
              onClick={toggleTheme}
              className="account-switch"
            />
          </div>
        </div>
      </SectionCard>
    </div>
  );
};

export default AccountPage;
